import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useClearSafetyFlag, useSafetyFlaggedUsers } from '@/hooks/use-admin'
import type { User } from '@/services/admin.service'
import { ShieldCheck } from 'lucide-react'

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export function AdminCrisisAlertsPage() {
  const { data: users, isLoading } = useSafetyFlaggedUsers()
  const clearFlag = useClearSafetyFlag()

  const handleClearFlag = async (user: User) => {
    if (
      confirm(
        `Clear safety flag for ${user.full_name || user.email || user.phone_number}? This indicates you have reviewed the situation.`,
      )
    ) {
      await clearFlag.mutateAsync(user.id)
    }
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Crisis Alerts</h1>
        <p className="text-muted-foreground">
          Users flagged for safety concerns during conversations
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Safety-Flagged Users</CardTitle>
          <CardDescription>{users?.length ?? 0} users currently flagged</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : users && users.length > 0 ? (
            <div className="rounded-md border">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="h-10 px-4 text-left text-sm font-medium">Name</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Email</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Phone</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Flagged At</th>
                    <th className="h-10 px-4 text-right text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b">
                      <td className="p-4">
                        <div className="font-medium">
                          {user.full_name || user.first_name || '-'}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">{user.email || '-'}</td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {user.phone_number || '-'}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {user.safety_flagged_at ? formatRelativeTime(user.safety_flagged_at) : '-'}
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleClearFlag(user)}
                          disabled={clearFlag.isPending}
                        >
                          <ShieldCheck className="h-4 w-4 mr-2" />
                          Clear Flag
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg font-medium">No safety-flagged users</p>
              <p className="text-sm mt-1">
                Users will appear here when crisis language is detected in conversations.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
